import './JourneyLaunchPopover.scss';

import React from 'react';

import { getIcon } from '@flybits/design-system';

import { UseJourneyActions } from '../hooks/useJourneyActions';
import useDraggablePopover from './useDraggablePopover';
import useLaunchPopoverState, { LaunchIconKind } from './useLaunchPopoverState';
import { ExperienceNameValidation } from '../FlowHeaderV2';

const MAIN_CLASS = 'journey-launch-popover';
const CLASSES = {
  HEADER: `${MAIN_CLASS}__header`,
  TITLE: `${MAIN_CLASS}__title`,
  ACTIONS: `${MAIN_CLASS}__actions`,
  COUNT: `${MAIN_CLASS}__count`,
  CLOSE: `${MAIN_CLASS}__close`,
  LIST: `${MAIN_CLASS}__list`,
  ITEM: `${MAIN_CLASS}__item`,
  BULLET: `${MAIN_CLASS}__bullet`,
  BODY: `${MAIN_CLASS}__body`,
  MESSAGE: `${MAIN_CLASS}__message`,
  LAUNCH_BTN: `${MAIN_CLASS}__launch-btn`,
  DEACTIVATE_BTN: `${MAIN_CLASS}__deactivate-btn`,
};

type JourneyLaunchPopoverProps = {
  actions: Pick<UseJourneyActions, 'launch' | 'deactivate' | 'isBusy'>;
  nameValidation: ExperienceNameValidation;
};

const launchGlyph = (icon: LaunchIconKind) =>
  icon === 'calendar'
    ? getIcon('calendar', { fill: '#ffffff', width: '24px', height: '24px' })
    : getIcon('rocket', { fill: '#ffffff', width: '24px', height: '24px' });

/**
 * Unified, draggable floating launch box for Flow v2. A single component renders one of five
 * states derived by {@link useLaunchPopoverState}: an error variant that replaces the launch box,
 * and the launch box itself in disabled / ready / live / scheduled forms. Dismissing the error
 * variant reveals the launch box underneath.
 *
 * Must remain inside `.flow-builder__flow-container` (the drag-bounds `offsetParent`).
 */
function JourneyLaunchPopover({ actions, nameValidation }: JourneyLaunchPopoverProps): JSX.Element {
  const { offset, onDragHandleMouseDown } = useDraggablePopover();
  const { state, dismissError } = useLaunchPopoverState();
  const { isBusy } = actions;
  const { experienceNameError } = nameValidation;
  const isSaveDisabled = isBusy || !!experienceNameError.length;

  const transform = `translate(calc(-50% + ${offset.x}px), ${offset.y}px)`;

  if (state.kind === 'error') {
    const count = state.errors.length;
    const countLabel = `${count} ${count === 1 ? 'problem' : 'problems'}`;

    return (
      <div className={`${MAIN_CLASS} ${MAIN_CLASS}--error`} role="alert" style={{ transform }}>
        <div className={CLASSES.HEADER} onPointerDown={onDragHandleMouseDown}>
          <span className={CLASSES.TITLE}>{state.title}</span>
          <div className={CLASSES.ACTIONS}>
            <span className={CLASSES.COUNT}>
              {countLabel}
              {getIcon('error', { fill: '#b32013' })}
            </span>
            <button type="button" className={CLASSES.CLOSE} aria-label="dismiss errors" onClick={dismissError}>
              {getIcon('clear', {})}
            </button>
          </div>
        </div>
        <ul className={CLASSES.LIST}>
          {state.errors.map((error) => (
            <li className={CLASSES.ITEM} key={error.stateIdx}>
              <span className={CLASSES.BULLET}>{getIcon('error', { fill: '#b32013' })}</span>
              <span>{error.err}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (state.kind === 'live' || state.kind === 'scheduled') {
    const isLive = state.kind === 'live';
    const message = isLive ? '🎉 Experience is live' : '📅 Experience is scheduled';

    return (
      <div
        className={`${MAIN_CLASS} ${MAIN_CLASS}--${state.kind}`}
        style={{ transform }}
        onPointerDown={onDragHandleMouseDown}
      >
        <div className={CLASSES.BODY}>
          <span className={CLASSES.MESSAGE}>{message}</span>
          <button type="button" className={CLASSES.DEACTIVATE_BTN} onClick={actions.deactivate}>
            {state.deactivateLabel}
          </button>
        </div>
      </div>
    );
  }

  // disabled | ready
  const isReady = state.kind === 'ready';
  const message = isReady ? 'Experience is ready to launch' : state.message;

  return (
    <div
      className={`${MAIN_CLASS} ${MAIN_CLASS}--${state.kind}`}
      style={{ transform }}
      onPointerDown={onDragHandleMouseDown}
    >
      <div className={CLASSES.BODY}>
        <span className={CLASSES.MESSAGE}>{message}</span>
        <button
          type="button"
          className={CLASSES.LAUNCH_BTN}
          disabled={!isReady || isSaveDisabled}
          aria-label={state.launchLabel}
          onClick={actions.launch}
        >
          <span className={CLASSES.BULLET}>{launchGlyph(state.launchIcon)}</span>
          <span>{state.launchLabel}</span>
        </button>
      </div>
    </div>
  );
}

export default JourneyLaunchPopover;
